import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { ROLE_LABELS, ROLE_COLORS, api } from "@/lib/api";
import {
  LayoutDashboard,
  Scroll,
  Megaphone,
  FileText,
  Users,
  Wallet,
  Bell,
  Shield,
  LogOut,
  User,
  Siren,
  UserX,
  FileClock,
  FileWarning,
  Image as ImageIcon,
  ClipboardList,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000;

const NAV_ITEMS = [
  {
    to: "/",
    label: "Panel",
    icon: LayoutDashboard,
    roles: ["founder", "admin", "officer", "member", "candidate"],
    testid: "nav-dashboard"
  },
  {
    to: "/rules",
    label: "Kurallar",
    icon: Scroll,
    roles: ["founder", "admin", "officer", "member", "candidate"],
    testid: "nav-rules"
  },
  {
    to: "/announcements",
    label: "Duyurular",
    icon: Megaphone,
    roles: ["founder", "admin", "officer", "member", "candidate"],
    testid: "nav-announcements"
  },
  {
    to: "/excuses",
    label: "Mazeretler",
    icon: FileText,
    roles: ["founder", "admin", "officer", "member"],
    testid: "nav-excuses"
  },
  {
    to: "/members",
    label: "Üyeler",
    icon: Users,
    roles: ["founder", "admin", "officer", "member"],
    testid: "nav-members"
  },
  {
    to: "/donate",
    label: "Donate",
    icon: Wallet,
    roles: ["founder", "admin", "officer", "member", "candidate"],
    testid: "nav-donate"
  },
  {
    to: "/notifications",
    label: "Bildirimler",
    icon: Bell,
    roles: ["founder", "admin", "officer", "member", "candidate"],
    testid: "nav-notifications"
  },
  {
    to: "/admin",
    label: "Yönetim",
    icon: Shield,
    roles: ["founder", "admin"],
    testid: "nav-admin"
  }
];

const MEMUR_NAV_ITEMS = [
  {
    to: "/memurlar",
    label: "Memurlar",
    icon: Siren,
    testid: "nav-memurlar"
  },
  {
    to: "/memur-mazeret",
    label: "Memur Mazeret",
    icon: FileClock,
    testid: "nav-memur-mazeret"
  },
  {
    to: "/olay-raporu",
    label: "Olay Raporu",
    icon: FileWarning,
    testid: "nav-olay-raporu"
  },
  {
    to: "/mugshot",
    label: "Mugshot",
    icon: ImageIcon,
    testid: "nav-mugshot"
  },
  {
    to: "/birim-basvuru",
    label: "Birim Başvuru",
    icon: ClipboardList,
    testid: "nav-birim-basvuru"
  },
  {
    to: "/memur-bilgi",
    label: "Memur Bilgi",
    icon: BookOpen,
    testid: "nav-memur-bilgi"
  },
  {
    to: "/ihrac-edilenler",
    label: "İhraç Edilenler",
    icon: UserX,
    testid: "nav-ihrac-edilenler"
  }
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const visibleNav = NAV_ITEMS.filter((n) =>
    n.roles.includes(user.role)
  );

  const [canSeeMemurlar, setCanSeeMemurlar] = useState(false);

  useEffect(() => {
    api
      .get("/memur/me")
      .then((r) =>
        setCanSeeMemurlar(!!r.data.can_see_memurlar)
      )
      .catch(() => setCanSeeMemurlar(false));
  }, []);

  const memurNav = canSeeMemurlar ? MEMUR_NAV_ITEMS : [];
  const allVisibleNav = [...visibleNav, ...memurNav];

  // Keep-alive
  useEffect(() => {
    const ping = () => {
      fetch(`${BACKEND_URL}/api/health`, {
        method: "GET",
        cache: "no-store"
      })
        .then(() =>
          console.log(
            "[keep-alive] ping ok",
            new Date().toISOString()
          )
        )
        .catch((err) =>
          console.warn("[keep-alive] ping failed", err)
        );
    };

    ping();

    const id = setInterval(
      ping,
      KEEPALIVE_INTERVAL_MS
    );

    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative min-h-screen text-zinc-100 overflow-hidden">

      {/* =========================================================
          GLOBAL BACKGROUND
          background.png -> frontend/public/background.png
      ========================================================== */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("/background.png")'
        }}
      />

      {/* Genel karartma */}
      <div className="fixed inset-0 bg-black/45" />

      {/* =========================================================
          SITE CONTENT
      ========================================================== */}
      <div className="relative z-10 flex min-h-screen w-full flex-col md:flex-row">

        {/* =======================================================
            SIDEBAR
        ======================================================== */}
        <aside className="hidden md:flex md:w-64 md:flex-col border-r border-white/10 bg-black/7 backdrop-blur-md sticky top-0 h-screen">

          {/* Logo */}
          <div className="p-6 border-b border-white/10">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 bg-rose-600 flex items-center justify-center font-heading font-bold text-white text-xl shadow-lg shadow-rose-600/20">
                D
              </div>

              <div>
                <div className="font-heading font-bold text-lg tracking-tight">
                  DEULSRA
                </div>

                <div className="text-[10px] font-mono text-zinc-400 tracking-widest">
                  GRUP // FIVEM
                </div>
              </div>

            </div>

          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">

            {allVisibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-rose-600/15 text-rose-400 border-l-2 border-rose-500"
                      : "text-zinc-300 hover:text-white hover:bg-black/30 border-l-2 border-transparent"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}

          </nav>

          {/* User / Logout */}
          <div className="p-3 border-t border-white/10 space-y-2">

            <NavLink
              to="/profile"
              data-testid="nav-profile"
              className="flex items-center gap-3 px-3 py-2 hover:bg-black/30 transition-colors"
            >

              {user.discord_avatar ? (
                <img
                  src={user.discord_avatar}
                  alt=""
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
              )}

              <div className="flex-1 min-w-0">

                <div className="text-sm font-medium truncate">
                  {user.display_name || user.discord_username}
                </div>

                <div
                  className={`text-[10px] font-mono px-1.5 py-0.5 inline-block border ${ROLE_COLORS[user.role]}`}
                >
                  {ROLE_LABELS[user.role]}
                </div>

              </div>

            </NavLink>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full justify-start text-zinc-300 hover:text-rose-400 hover:bg-rose-950/30"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>

          </div>

        </aside>

        {/* =======================================================
            MOBILE HEADER
        ======================================================== */}
        <header className="md:hidden sticky top-0 z-30 bg-black/25 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">

          <div className="flex items-center gap-2">

            <div className="w-8 h-8 bg-rose-600 flex items-center justify-center font-heading font-bold text-white shadow-lg shadow-rose-600/20">
              S
            </div>

            <div className="font-heading font-bold tracking-tight">
              DEULSRA
            </div>

          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4" />
          </Button>

        </header>

        {/* =======================================================
            MAIN CONTENT
        ======================================================== */}
        <main className="flex-1 min-w-0 pb-20 md:pb-0">
          <Outlet />
        </main>

        {/* =======================================================
            MOBILE BOTTOM NAV
        ======================================================== */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-black/30 backdrop-blur-xl border-t border-white/10 grid grid-cols-5">

          {allVisibleNav.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2.5 text-[10px] gap-1 ${
                  isActive
                    ? "text-rose-400"
                    : "text-zinc-400"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}

        </nav>

      </div>
    </div>
  );
}